import React, { useContext, useEffect, useState } from 'react';
import $ from 'jquery';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Box, Button, Stack, TextField } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import Toast from '../../../../components/Toast';
import { useAuth } from '../../../../hooks/Auth';
import { useIntegration } from '../../../../hooks/Integration';
import { useModal } from '../../../../hooks/Modal';
import { options } from '../../../../helpers/toastOptions';
import { parseStringWithRegex } from '../../../../helpers/parseString';
import { parseCpfCnpj, parsePhone } from '../../../../helpers/parseValues';
import { ReverseCollectContext } from '../..';
import { ModalTitle } from './styled';

export const ModalAddCollectAddress = () => {
  const [senderDocument, setSenderDocument] = useState('');
  const [senderZipPostalCode, setSenderZipPostalCode] = useState('');
  const [senderName, setSenderName] = useState('');
  const [senderAddress, setSenderAddress] = useState('');
  const [senderAddressNumber, setSenderAddressNumber] = useState('');
  const [senderAdditionalInformation, setSenderAdditionalInformation] =
    useState('');
  const [senderSuburb, setSenderSuburb] = useState('');
  const [senderCity, setSenderCity] = useState('');
  const [senderState, setSenderState] = useState('');
  // const [zipSearchedByDocument, setZipSearchedByDocument] = useState('');
  const [hasInvalidZipCode, setHasInvalidZipCode] = useState(false);
  const [disableName, setDisableName] = useState(false);
  const [disableStreetAddress, setDisableStreetAddress] = useState(true);
  const [disableSuburb, setDisableSuburb] = useState(true);
  const [isToValidateDocument, setIsToValidateDocument] = useState(true);
  const [isToValidateAddressNumber, setIsToValidateAddressNumber] =
    useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [zipSearchedByDocument, setZipSearchedByDocument] = useState('');

  const { token } = useAuth();
  const { searchUser, getAddressByZipPostalCode } = useIntegration();
  const { Modal, closeModal } = useModal();

  const maxSM = useMediaQuery('(max-width:768px)');
  const maxMD = useMediaQuery('(max-width:900px)');

  const {
    addressSenderList,
    setAddressSenderList,
    setSelectedAddressSenderIndex,
    setIsToValidateSenderAddressNumber,
    setCollectSenderData,
    openChangeCollectAddressModal,
    dataToAddCollectAddressModal,
    setDataToAddCollectAddressModal
  } = useContext(ReverseCollectContext);

  const addNewAddressSchema = Yup.object().shape({
    document: Yup.string()
      .required('CNPJ/CPF obrigatório')
      .trim('CNPJ/CPF obrigatório')
      .test('validadeDocument', 'CNPJ/CPF inválido', (value) => {
        if (isToValidateDocument) {
          return (
            typeof value === 'string' &&
            [11, 14].includes(parseCpfCnpj(value)?.length)
          );
        }

        return true;
      }),
    name: Yup.string()
      .required('Razão Social / Nome obrigatório')
      .trim('Razão Social / Nome obrigatório'),
    zipPostalCode: Yup.string()
      .required('CEP obrigatório')
      .trim('CEP obrigatório')
      .test('validateZipPostalCode', 'CEP inválido', (value) => {
        const regex = /^\d{8}$/;
        return (
          value && value.replace('-', '').match(regex) && !hasInvalidZipCode
        );
      }),
    streetAddress: Yup.string()
      .required('Endereço obrigatório')
      .trim('Endereço obrigatório'),
    streetAddressNumber: Yup.string()
      .required('Número obrigatório')
      .trim('Número obrigatório')
      .test('validateStreetAddressNumber', 'Número inválido', (value) => {
        if (!isToValidateAddressNumber) {
          return true;
        }

        return value && value !== '0';
      }),
    additionalInformation: Yup.string(),
    suburb: Yup.string()
      .required('Bairro obrigatório')
      .trim('Bairro obrigatório'),
    city: Yup.string()
      .required('Cidade obrigatória')
      .trim('Cidade obrigatória'),
    state: Yup.string()
      .required('Estado obrigatório')
      .trim('Estado obrigatório')
  });

  const {
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(addNewAddressSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange'
  });

  const onSubmit = async ({
    document,
    name,
    zipPostalCode,
    streetAddress,
    streetAddressNumber,
    additionalInformation,
    suburb,
    city,
    state
  }) => {
    try {
      setIsSubmitting(true);
      if (!suburb) {
        setDisableSuburb(false);
      }

      const getAddressResponse = await getAddressByZipPostalCode({
        zipPostalCode: parseCpfCnpj(zipPostalCode)
      });

      if (!getAddressResponse) {
        setError('zipPostalCode', {
          message: 'CEP inválido'
        });

        setIsSubmitting(false);
        return;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      setIsSubmitting(false);
    }

    const indexAlreadyExists = addressSenderList.findIndex(
      (value) =>
        name?.toLowerCase() === value?.name?.toLowerCase() &&
        document === value?.document &&
        additionalInformation?.toLowerCase() ===
          value?.address?.additionalInformation?.toLowerCase() &&
        city?.toLowerCase() === value?.address?.city?.toLowerCase() &&
        state?.toLowerCase() === value?.address?.state?.toLowerCase() &&
        streetAddress?.toLowerCase() ===
          value?.address?.streetAddress?.toLowerCase() &&
        streetAddressNumber?.toLowerCase() ===
          value?.address?.streetAddressNumber?.toLowerCase() &&
        suburb?.toLowerCase() === value?.address?.suburb?.toLowerCase() &&
        parsePhone(zipPostalCode) === value?.address?.zipPostalCode
    );

    if (indexAlreadyExists < 0) {
      const olderLength = addressSenderList?.length;

      const newAddressSenderList = [
        ...addressSenderList,
        {
          document,
          name,
          address: {
            zipPostalCode: parsePhone(zipPostalCode),
            streetAddress,
            streetAddressNumber,
            additionalInformation,
            suburb,
            city,
            state
          }
        }
      ];

      setAddressSenderList(newAddressSenderList);

      setCollectSenderData(newAddressSenderList[olderLength]);
      setSelectedAddressSenderIndex(olderLength);
    } else {
      setCollectSenderData(addressSenderList[indexAlreadyExists]);
      setSelectedAddressSenderIndex(indexAlreadyExists);
    }

    setDataToAddCollectAddressModal({});
    setIsToValidateSenderAddressNumber(false);
    closeModal();
  };

  const getDocumentInfo = async (value) => {
    setIsToValidateDocument(true);
    setIsToValidateAddressNumber(true);

    if (value) {
      try {
        const localToken = localStorage.getItem('@Braspress:token');

        const response = await searchUser({
          searchData: { document: parseCpfCnpj(value) },
          token: localToken
        });

        if (response?.length > 0) {
          const userSearched = response[0];

          if (userSearched?.document) {
            const {
              name,
              zipPostalCode,
              streetAddress,
              streetAddressNumber,
              addressComplement,
              suburb,
              city,
              state
            } = userSearched;

            setValue('name', name.toUpperCase());
            setSenderName(name.toUpperCase());

            setValue('zipPostalCode', zipPostalCode);
            setSenderZipPostalCode(zipPostalCode);
            setZipSearchedByDocument(zipPostalCode);

            const newStringAddress = parseStringWithRegex({
              string: String(streetAddress).trim(),
              regex: /^[aA-zZ0-9\s]+$/
            });

            setValue('streetAddress', newStringAddress.toUpperCase());
            setSenderAddress(newStringAddress.toUpperCase());

            setValue('streetAddressNumber', streetAddressNumber);
            setSenderAddressNumber(streetAddressNumber);

            if (addressComplement) {
              const newStringAddressComplement = parseStringWithRegex({
                string: String(addressComplement).trim(),
                regex: /^[aA-zZ0-9\s]+$/
              });

              setValue('additionalInformation', newStringAddressComplement);
              setSenderAdditionalInformation(newStringAddressComplement);
            } else {
              setValue('additionalInformation', '');
              setSenderAdditionalInformation('');
            }

            if (suburb) {
              const newStringSuburb = parseStringWithRegex({
                string: String(suburb).trim(),
                regex: /^[aA-zZ\s]+$/
              });

              setValue('suburb', newStringSuburb.toUpperCase());
              setSenderSuburb(newStringSuburb.toUpperCase());
              setDisableSuburb(true);
            } else {
              setDisableSuburb(false);
              setValue('suburb', '');
              setSenderSuburb('');
            }

            setValue('city', city.toUpperCase());
            setSenderCity(city.toUpperCase());

            setValue('state', state.toUpperCase());
            setSenderState(state.toUpperCase());

            setHasInvalidZipCode(false);
            setDisableName(true);
            setDisableStreetAddress(true);
            setIsToValidateDocument(false);

            clearErrors([
              'document',
              'name',
              'zipPostalCode',
              'streetAddress',
              'streetAddressNumber',
              'additionalInformation',
              'suburb',
              'city',
              'state'
            ]);
          }
        } else {
          setDisableName(false);

          toast(
            <Toast
              title="Atenção!"
              content="Usuário não foi localizado com o documento informado."
              type="error"
            />,
            options
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      }
    } else {
      setDisableName(false);
      setValue('name', '');
      setSenderName('');

      if (!senderZipPostalCode) {
        setValue('streetAddress', '');
        setSenderAddress('');
        setValue('streetAddressNumber', '');
        setSenderAddressNumber('');
        setValue('additionalInformation', '');
        setSenderAdditionalInformation('');
        setValue('suburb', '');
        setSenderSuburb('');
        setValue('city', '');
        setSenderCity('');
        setValue('state', '');
        setSenderState('');
      }
    }
  };

  const searchAddressByZipPostalCode = async (value) => {
    const regex = /^\d{8}$/;

    if (value === zipSearchedByDocument) {
      return;
    }

    if (value.replace('-', '').match(regex)) {
      // if (value.replace('-', '') === zipSearchedByDocument) {
      //   return;
      // }

      try {
        const data = await getAddressByZipPostalCode({
          zipPostalCode: value.replace('-', ''),
          token
        });

        if (data) {
          const clear = ['zipPostalCode'];

          setHasInvalidZipCode(false);

          if (data.streetAddress) {
            const newStringAddress = parseStringWithRegex({
              string: String(data.streetAddress).trim(),
              regex: /^[aA-zZ0-9\s]+$/
            });

            if (newStringAddress) {
              setSenderAddress(newStringAddress?.toUpperCase());
              setValue('streetAddress', newStringAddress?.toUpperCase());
              clear.push('streetAddress');
              setDisableStreetAddress(true);
            } else {
              setSenderAddress('');
              setValue('streetAddress', '');
              setDisableStreetAddress(false);
            }
          } else {
            setValue('streetAddress', '');
            setSenderAddress('');
            setDisableStreetAddress(false);
          }

          if (data.suburb) {
            const newStringSuburb = parseStringWithRegex({
              string: String(data.suburb).trim(),
              regex: /^[aA-zZ\s]+$/
            });

            if (newStringSuburb) {
              setSenderSuburb(newStringSuburb?.toUpperCase());
              setValue('suburb', newStringSuburb?.toUpperCase());
              clear.push('suburb');
              setDisableSuburb(true);
            } else {
              setSenderSuburb('');
              setValue('suburb', '');
              setDisableSuburb(false);
            }
          } else {
            setValue('suburb', '');
            setSenderSuburb('');
            setDisableSuburb(false);
          }

          if (data.city) {
            setValue('city', data.city.toUpperCase());
            setSenderCity(data.city.toUpperCase());
            clear.push('city');
          } else {
            setValue('city', '');
            setSenderCity('');
          }

          if (data.state) {
            setValue('state', data.state.toUpperCase());
            setSenderState(data.state.toUpperCase());
            clear.push('state');
          } else {
            setValue('state', '');
            setSenderState('');
          }

          setValue('streetAddressNumber', '');
          setSenderAddressNumber('');
          setValue('additionalInformation', '');
          setSenderAdditionalInformation('');

          clearErrors(clear);
        } else {
          setError('zipPostalCode', {
            message: 'CEP inválido'
          });
          setHasInvalidZipCode(true);
          setDisableStreetAddress(true);
          setDisableSuburb(true);
          setValue('streetAddress', '');
          setSenderAddress('');
          setValue('streetAddressNumber', '');
          setSenderAddressNumber('');
          setValue('additionalInformation', '');
          setSenderAdditionalInformation('');
          setValue('suburb', '');
          setSenderSuburb('');
          setValue('city', '');
          setSenderCity('');
          setValue('state', '');
          setSenderState('');

          toast(
            <Toast
              title="Atenção!"
              content="O CEP informado não foi localizado."
              type="error"
            />,
            options
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      }
    } else {
      setDisableStreetAddress(true);
      setDisableSuburb(true);
      setValue('streetAddress', '');
      setSenderAddress('');
      setValue('streetAddressNumber', '');
      setSenderAddressNumber('');
      setValue('additionalInformation', '');
      setSenderAdditionalInformation('');
      setValue('suburb', '');
      setSenderSuburb('');
      setValue('city', '');
      setSenderCity('');
      setValue('state', '');
      setSenderState('');
    }
  };

  useEffect(() => {
    const cpfCnpjOptions = {
      onKeyPress(cpf, _ev, _el, op) {
        const masks = ['000.000.000-000', '00.000.000/0000-00'];
        $('#senderDocument').mask(cpf.length > 14 ? masks[1] : masks[0], op);
      }
    };

    $('#senderDocument').length > 11
      ? $('#senderDocument').mask('00.000.000/0000-00', cpfCnpjOptions)
      : $('#senderDocument').mask('000.000.000-00#', cpfCnpjOptions);
  }, [senderDocument]);

  useEffect(() => {
    $('#senderZipPostalCode').mask('00000-000');
  }, [senderZipPostalCode]);

  useEffect(() => {
    if (dataToAddCollectAddressModal?.document) {
      setValue('document', dataToAddCollectAddressModal.document);
      setSenderDocument(dataToAddCollectAddressModal.document);

      setValue('name', dataToAddCollectAddressModal.name);
      setSenderName(dataToAddCollectAddressModal.name);

      setValue('zipPostalCode', dataToAddCollectAddressModal.zipPostalCode);
      setSenderZipPostalCode(dataToAddCollectAddressModal.zipPostalCode);

      setValue('streetAddress', dataToAddCollectAddressModal.streetAddress);
      setSenderAddress(dataToAddCollectAddressModal.streetAddress);

      setValue(
        'streetAddressNumber',
        dataToAddCollectAddressModal.streetAddressNumber
      );
      setSenderAddressNumber(dataToAddCollectAddressModal.streetAddressNumber);

      setValue(
        'additionalInformation',
        dataToAddCollectAddressModal.additionalInformation
      );
      setSenderAdditionalInformation(
        dataToAddCollectAddressModal.additionalInformation
      );

      setValue('suburb', dataToAddCollectAddressModal.suburb);
      setSenderSuburb(dataToAddCollectAddressModal.suburb);

      setValue('city', dataToAddCollectAddressModal.city);
      setSenderCity(dataToAddCollectAddressModal.city);

      setValue('state', dataToAddCollectAddressModal.state);
      setSenderState(dataToAddCollectAddressModal.state);

      setIsToValidateDocument(false);

      if (dataToAddCollectAddressModal.hasInvalidZipCode) {
        setHasInvalidZipCode(true);
        setError('zipPostalCode', {
          message: 'CEP inválido'
        });
      }

      if (dataToAddCollectAddressModal?.hasInvalidStreetAddressNumber) {
        setError('streetAddressNumber', {
          message: 'Número inválido'
        });
      }
    }
  }, []);

  return (
    <Modal preventClose>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            maxHeight: '86vh',
            overflow: 'auto'
          }}
        >
          <ModalTitle>ADICIONAR NOVO ENDEREÇO</ModalTitle>

          <Stack spacing={2} style={{ marginTop: '8px' }}>
            <Stack direction={maxMD ? 'column' : 'row'} spacing={2}>
              <Controller
                name="document"
                control={control}
                defaultValue=""
                render={({ field }) => {
                  return (
                    <TextField
                      label="CNPJ / CPF *"
                      id="senderDocument"
                      data-testid="newCollectAddressDocumentInput"
                      style={{ minWidth: 240 }}
                      {...field}
                      disabled={isSubmitting}
                      value={(field.value = senderDocument)}
                      onChange={(e) => {
                        setIsToValidateDocument(true);
                        field.onChange(e.target.value);
                        setSenderDocument(e.target.value);
                      }}
                      onBlur={(e) => {
                        getDocumentInfo(e.target.value);
                        field.onBlur(field.onChange(e.target.value));
                        setSenderDocument(e.target.value);
                      }}
                      error={!!errors.document}
                      helperText={errors.document?.message}
                    />
                  );
                }}
              />

              <Controller
                name="name"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Razão Social/Nome *"
                    style={{ width: '100%' }}
                    {...field}
                    value={(field.value = senderName)}
                    disabled={disableName || isSubmitting}
                    onChange={(e) => {
                      field.onChange(e.target.value.toUpperCase());
                      setSenderName(e.target.value.toUpperCase());
                    }}
                    onBlur={(e) => {
                      field.onBlur(
                        field.onChange(e.target.value.toUpperCase())
                      );
                      setSenderName(e.target.value.toUpperCase());
                    }}
                    inputProps={{ maxLength: 256 }}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Stack>

            <Stack direction={maxSM ? 'column' : 'row'} spacing={2}>
              <Controller
                name="zipPostalCode"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <TextField
                    label="CEP *"
                    id="senderZipPostalCode"
                    style={{ width: !maxMD && 240 }}
                    {...field}
                    disabled={isSubmitting}
                    value={(field.value = senderZipPostalCode)}
                    onChange={(e) => {
                      setHasInvalidZipCode(true);
                      field.onChange(e.target.value);
                      setSenderZipPostalCode(e.target.value);
                    }}
                    onBlur={(e) => {
                      const { value } = e.target;
                      searchAddressByZipPostalCode(value);

                      field.onBlur(field.onChange(value));
                      setSenderZipPostalCode(value);
                    }}
                    error={!!errors.zipPostalCode}
                    helperText={errors.zipPostalCode?.message}
                  />
                )}
              />

              <Controller
                name="streetAddress"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Endereço *"
                    style={{ width: maxMD && '100%', flex: !maxMD && '1' }}
                    {...field}
                    value={(field.value = senderAddress)}
                    disabled={!!disableStreetAddress || isSubmitting}
                    onChange={(e) => {
                      field.onChange(e.target.value.toUpperCase());
                      setSenderAddress(e.target.value.toUpperCase());
                    }}
                    onBlur={(e) => {
                      field.onBlur(
                        field.onChange(e.target.value.toUpperCase())
                      );
                      setSenderAddress(e.target.value.toUpperCase());
                    }}
                    inputProps={{ maxLength: 256 }}
                    error={!!errors.streetAddress}
                    helperText={errors.streetAddress?.message}
                  />
                )}
              />

              <Controller
                name="streetAddressNumber"
                defaultValue=""
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Número *"
                    style={{ width: maxSM ? '100%' : 96, minWidth: 96 }}
                    {...field}
                    disabled={isSubmitting}
                    value={(field.value = senderAddressNumber)}
                    onChange={(e) => {
                      const regex = /^[0-9]+$/;
                      if (
                        !e.target.value.match(regex) &&
                        e.target.value !== ''
                      ) {
                        return;
                      }

                      setIsToValidateAddressNumber(false);
                      field.onChange(e.target.value);
                      setSenderAddressNumber(e.target.value);
                    }}
                    onBlur={(e) => {
                      field.onBlur(field.onChange(e.target.value));
                      setSenderAddressNumber(e.target.value);
                    }}
                    inputProps={{ maxLength: 50 }}
                    error={!!errors.streetAddressNumber}
                    helperText={errors.streetAddressNumber?.message}
                  />
                )}
              />
            </Stack>

            <Stack direction={maxMD ? 'column' : 'row'} spacing={2}>
              <Stack
                direction={maxSM ? 'column' : 'row'}
                spacing={2}
                style={{ width: maxMD ? '100%' : '55%' }}
              >
                <Controller
                  name="additionalInformation"
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Complemento"
                      style={{ width: !maxMD && 240 }}
                      fullWidth={maxMD}
                      {...field}
                      disabled={isSubmitting}
                      value={(field.value = senderAdditionalInformation)}
                      onChange={(e) => {
                        const regex = /^[aA-zZ0-9\s]+$/;
                        if (
                          !e.target.value.match(regex) &&
                          e.target.value !== ''
                        ) {
                          return;
                        }
                        field.onChange(e.target.value.toUpperCase());
                        setSenderAdditionalInformation(
                          e.target.value.toUpperCase()
                        );
                      }}
                      onBlur={(e) => {
                        field.onBlur(
                          field.onChange(e.target.value.toUpperCase())
                        );
                        setSenderAdditionalInformation(
                          e.target.value.toUpperCase()
                        );
                      }}
                      inputProps={{ maxLength: 256 }}
                      error={!!errors.additionalInformation}
                      helperText={errors.additionalInformation?.message}
                    />
                  )}
                />

                <Controller
                  name="suburb"
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Bairro *"
                      style={{ flex: !maxMD && '1' }}
                      fullWidth={maxMD}
                      {...field}
                      value={(field.value = senderSuburb)}
                      disabled={disableSuburb || isSubmitting}
                      onChange={(e) => {
                        const regex = /^[aA-zZ\s]+$/;
                        if (
                          !e.target.value.match(regex) &&
                          e.target.value !== ''
                        ) {
                          return;
                        }
                        field.onChange(e.target.value.toUpperCase());
                        setSenderSuburb(e.target.value.toUpperCase());
                      }}
                      onBlur={(e) => {
                        field.onBlur(
                          field.onChange(e.target.value.toUpperCase())
                        );
                        setSenderSuburb(e.target.value.toUpperCase());
                      }}
                      inputProps={{ maxLength: 50 }}
                      error={!!errors.suburb}
                      helperText={errors.suburb?.message}
                    />
                  )}
                />
              </Stack>

              <Stack
                direction="row"
                spacing={2}
                style={{ width: maxMD ? '100%' : '45%' }}
              >
                <Controller
                  name="city"
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Cidade *"
                      style={{ flex: !maxMD && '1' }}
                      fullWidth={maxMD}
                      {...field}
                      value={(field.value = senderCity)}
                      disabled
                      onChange={(e) => {
                        field.onChange(e.target.value.toUpperCase());
                        setSenderCity(e.target.value.toUpperCase());
                      }}
                      onBlur={(e) => {
                        field.onBlur(
                          field.onChange(e.target.value.toUpperCase())
                        );
                        setSenderCity(e.target.value.toUpperCase());
                      }}
                      inputProps={{ maxLength: 50 }}
                      error={!!errors.city}
                      helperText={errors.city?.message}
                    />
                  )}
                />

                <Controller
                  name="state"
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="UF *"
                      style={{ width: 96, minWidth: 96 }}
                      {...field}
                      value={(field.value = senderState)}
                      disabled
                      onChange={(e) => {
                        const regex = /^[aA-zZ\s]+$/;
                        if (
                          !e.target.value.match(regex) &&
                          e.target.value !== ''
                        ) {
                          return;
                        }
                        field.onChange(e.target.value.toUpperCase());
                        setSenderState(e.target.value.toUpperCase());
                      }}
                      onBlur={(e) => {
                        field.onBlur(
                          field.onChange(e.target.value.toUpperCase())
                        );
                        setSenderState(e.target.value.toUpperCase());
                      }}
                      inputProps={{ maxLength: 2 }}
                      error={!!errors.state}
                      helperText={errors.state?.message}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2} style={{ marginTop: '8px' }}>
            <Box
              style={{ display: 'flex', flex: 1, justifyContent: 'flex-end' }}
            >
              <Button
                type="button"
                variant="contained"
                color="secondary"
                onClick={() => {
                  setDataToAddCollectAddressModal({});
                  openChangeCollectAddressModal();
                }}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
            </Box>

            <Box style={{ flex: 1 }}>
              <LoadingButton
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                Salvar
              </LoadingButton>
            </Box>
          </Stack>
        </Box>
      </form>
    </Modal>
  );
};
